import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    type CreateUserMutation,
    CreateUserDocument,
    type CreateUserMutationVariables,
} from '../../../api/mutations/createUser';
import useLoadingButton from '../../../components/button/useLoadingButton';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import UserForm, { defaultValues, type UserFormValues, useUserFormValidator } from '../UserDetailsPage/UserForm';

const AddUserPage = () => {
    const { t } = useTranslation(['userDetails']);
    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();
    const validate = useUserFormValidator();

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<UserFormValues>(
        async settings => {
            setLoading(true);
            // submitting message
            message.loading({
                content: t('userDetails:messages.creationSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient
                .mutate<CreateUserMutation, CreateUserMutationVariables>({
                    mutation: CreateUserDocument,
                    variables: { settings },
                })
                .finally(() => {
                    message.destroy('primary');
                    setLoading(false);
                });

            // inform about success
            message.success({
                content: t('userDetails:messages.creationSuccessful'),
                key: 'primary',
            });
            setLoading(false);
            // then go back to list
            navigate(`/admin/accesses/users`);
        },
        [apolloClient, navigate, setLoading, t]
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="createUserForm" htmlType="submit" type="primary">
                    {t('userDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/accesses/users')}
            title={t('userDetails:addTitle')}
        >
            <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="createUserForm" name="createUserForm" onSubmitCapture={handleSubmit}>
                        <UserForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddUserPage;
